/* ============================================================
   ApiStore — the data layer. Offline-first: local state is the thing the UI
   reads, disk is what makes it survive a launch, and the server is where it
   eventually agrees with every other device.

   Four moving parts:

     1. STATE — one AppState, mutated only through the pure transforms in
        store/logic.ts, so an optimistic change and the server's own computation
        of it run the same code. Snapshotted to disk (store/persist.ts) so a cold
        launch with no signal paints the last known data instead of an empty app.

     2. OUTBOX — every mutation is also appended to a durable queue as a
        serializable description of intent (store/outbox.ts) and drained IN
        ORDER, one at a time. A tap made offline is a queued entry, not a lost
        request; the queue is written to disk before the network is touched, so a
        force-quit mid-flight costs nothing. An entry only leaves the queue when
        the server acknowledges it (or refuses it on its merits).

     3. RECONCILIATION — a create inserts its row under a launch-unique temp id
        immediately and swaps it for the server's id when the POST returns; any
        pin, patch, or resolution still queued behind that create is rewritten to
        the real id at the same moment (outbox.remapIds). So a card never blocks
        on the network yet always ends up addressable by its real id.

     4. LIVENESS — all of the above is invisible by construction (an offline tap
        looks exactly like an online one), so every phase change is published on
        a SEPARATE context (store/sync.ts) for the corner sync badge: syncing
        while a drain or pull is in flight, offline when fetch never completed,
        error when the server answered with a failure, plus how much is still
        queued. Separate precisely because it changes on every drain — folding it
        into StoreValue would re-render every screen to spin one 17px glyph.

   Failure handling is the part worth reading twice. The old contract was
   "any failure → full resync", which silently threw away the write the user just
   made. Now a failure is classified (outbox.dispositionFor): one that never
   reached the handler waits and retries forever, one the server keeps erroring on
   is retried a bounded number of times, and one the server refuses on its merits
   is dropped and heals with a full pull. A full pull REPLACES local state, so it
   only ever runs with an empty queue — otherwise it would overwrite the very
   writes still waiting to be sent.

   Network I/O lives ONLY here (and in outbox.performOp); the transforms and
   selectors are pure. Disk and the app lifecycle come in through the platform
   adapters (ClientCoreProvider): `usePersist()` for the snapshot and queue,
   `adapters.lifecycle` for the foreground/background edges that kick a drain
   and flush the snapshot. Nothing here knows which platform it is on.
   ============================================================ */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type HabitSchedule, presetToRRule } from '@habishine/shared';
import { useAuth } from '../auth/AuthContext';
import { useEntitlement } from '../billing/EntitlementContext';
import { useWeekStart } from '../preferences/PreferencesContext';
import { ApiError } from '../api/client';
import { useApi } from '../api/useApi';
import { useClientCore, usePersist } from '../provider';
import type {
  ServerHabit,
  ServerProject,
  ServerTag,
  ServerTask,
  SyncResponse,
  TaskResolution,
} from '../api/types';
import { todayStr } from '../dates';
import type { HueName } from '../hues';
import type { AppState, CaptureState, Habit, Project, Scope, Tag, Task } from '../types';
import { StoreContext } from './context';
import { SyncContext, type SyncPhase, type SyncStatus } from './sync';
import type { StoreValue, ReplanWhere } from './types';
import { toAppState, toHabit, toProject, toTag, toTask } from './mapping';
import * as logic from './logic';
import {
  dispositionFor,
  isGone,
  performOp,
  remapIds,
  type OpHandlers,
  type OutboxEntry,
  type OutboxOp,
} from './outbox';

/** default "do first" cap — the server has no cap concept yet, so it's local. */
const DEFAULT_CAP = 5;

/** Coalescing window for snapshot writes. The timer reads the freshest state when
   it fires, so a burst of taps costs one write rather than one per tap. */
const SAVE_DEBOUNCE_MS = 500;

/** Retry backoff for a blocked queue: 1s, 2s, 4s … capped. A foreground return or
   a new mutation also kicks the queue, so this is the floor on how long an
   offline device waits, not the only way it wakes up. */
const RETRY_BASE_MS = 1_000;
const RETRY_MAX_MS = 60_000;

/** How many times the SERVER may actively reject an entry before we give up on it
   and let the queue behind it move. Attempts that never reached the server
   (offline) are not counted — see OutboxEntry.tries. */
const MAX_SERVER_REJECTS = 6;

/** Which sync phase a failed request should surface. A non-ApiError means fetch
   itself rejected, so nothing reached the server — the offline case. Anything
   else IS an answer from the server, however unwelcome. */
function phaseForError(err: unknown): SyncPhase {
  return err instanceof ApiError ? 'error' : 'offline';
}

/** Distinguishes temp ids minted this launch from ones restored off disk — the
   outbox outlives the counter, so a bare sequence could hand out an id a queued
   entry is already using. */
const LAUNCH_TAG = Math.random().toString(36).slice(2, 8);

/** Replace a task by id, or append when absent (a merge of a server response). */
function upsertTask(project: Task[], t: Task): Task[] {
  const i = project.findIndex((x) => x.id === t.id);
  if (i < 0) return [...project, t];
  const next = [...project];
  next[i] = t;
  return next;
}

function upsertProject(project: Project[], l: Project): Project[] {
  const i = project.findIndex((x) => x.id === l.id);
  if (i < 0) return [...project, l];
  const next = [...project];
  next[i] = l;
  return next;
}

function upsertTag(project: Tag[], t: Tag): Tag[] {
  const i = project.findIndex((x) => x.id === t.id);
  if (i < 0) return [...project, t];
  const next = [...project];
  next[i] = t;
  return next;
}

function upsertHabit(project: Habit[], h: Habit): Habit[] {
  const i = project.findIndex((x) => x.id === h.id);
  if (i < 0) return [...project, h];
  const next = [...project];
  next[i] = h;
  return next;
}

/** Replace the row under `oldId` (a temp id) with the server's, or the row that
   already carries the server id — appending when neither is present. */
function replaceOrAppend(project: Task[], oldId: string, t: Task): Task[] {
  const i = project.findIndex((x) => x.id === oldId || x.id === t.id);
  if (i < 0) return [...project, t];
  const next = [...project];
  next[i] = t;
  return next;
}

/** Point every pin that referenced `oldId` at `newId` (id reconciliation). */
function renamePin(
  pinned: Record<string, string | null>,
  oldId: string,
  newId: string,
): Record<string, string | null> {
  let changed = false;
  const next: Record<string, string | null> = { ...pinned };
  for (const d of Object.keys(pinned)) {
    if (pinned[d] === oldId) {
      next[d] = newId;
      changed = true;
    }
  }
  return changed ? next : pinned;
}

const EMPTY: AppState = { cap: DEFAULT_CAP, habits: [], tasks: [], projects: [], tags: [], pinnedByDay: {}, checkins: {} };

export function ApiStoreProvider({
  children,
  /** What to render until the cache has been read (or, with no cache, until
     the first pull answers). The front end owns the placeholder — on native it
     also holds the launch splash up. */
  fallback,
}: {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  const api = useApi();
  const persist = usePersist();
  const { adapters } = useClientCore();
  const { analytics, lifecycle } = adapters;
  const { user } = useAuth();
  // Persistence is namespaced per account, so two users on one device never read
  // each other's cache. A token without a `sub` claim shouldn't happen (Cognito
  // always issues one), but if it did, running without a cache is the safe
  // failure — never a shared one.
  const userKey = user?.sub ?? null;
  // Which weekday a week begins on — the habit cadence needs it to bound the live
  // week ("2 of 3 this week"). Every callback that derives habit state takes it as
  // a dependency, so changing the preference re-identifies them and the screens
  // re-derive rather than showing a week scored against the old boundary.
  const { weekStartsOn } = useWeekStart();
  const [state, setState] = useState<AppState>(EMPTY);
  const [ready, setReady] = useState(false);

  // Liveness, published on its own context (store/sync.ts) so the corner badge
  // re-renders without dragging every screen with it. The ref is the truth —
  // drain and pull update it from callbacks that must not close over stale
  // state — and the snapshot is what React sees.
  const syncRef = useRef<Omit<SyncStatus, 'syncNow'>>({
    phase: 'synced',
    pending: 0,
    lastSyncedAt: null,
  });
  const [syncSnap, setSyncSnap] = useState(syncRef.current);

  /** Publish a sync-status change, skipping the render when nothing moved. */
  const setSync = useCallback((patch: Partial<Omit<SyncStatus, 'syncNow'>>) => {
    const cur = syncRef.current;
    const next = { ...cur, ...patch };
    if (
      next.phase === cur.phase &&
      next.pending === cur.pending &&
      next.lastSyncedAt === cur.lastSyncedAt
    ) {
      return;
    }
    syncRef.current = next;
    setSyncSnap(next);
  }, []);

  // Freshest state, updated synchronously on every write so back-to-back
  // optimistic transforms and async merges all build on the latest snapshot.
  const stateRef = useRef(state);
  // Stable API handle for the callbacks below (keeps them from re-identifying
  // when the auth token refreshes and useApi returns a new client).
  const apiRef = useRef(api);
  apiRef.current = api;
  // Same trick as apiRef: `pullAll` is a stable callback, so it must not close
  // over an `adopt` identity that changes. EntitlementProvider sits ABOVE this
  // provider in the tree, so the hook is always available here.
  const { adopt: adoptPlan } = useEntitlement();
  const adoptPlanRef = useRef(adoptPlan);
  adoptPlanRef.current = adoptPlan;
  // Monotonic counter for temp ids handed out before a create round-trips.
  const tempSeq = useRef(1);

  /* ---------- durable write queue ---------- */
  const queueRef = useRef<OutboxEntry[]>([]);
  const seqRef = useRef(1);
  // One drain at a time — concurrent drains would break in-order delivery. Held
  // as the in-flight promise rather than a flag so a second caller can AWAIT the
  // running drain instead of skipping past it.
  const drainPromiseRef = useRef<Promise<void> | null>(null);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const backoffRef = useRef(0);
  // The PULL needs a retry timer of its own. The queue's only fires while
  // something is queued, so a launch that came up offline with nothing to send
  // would otherwise sit on cached data until the user happened to background
  // the app — the one state where "it retries by itself" would have been a lie.
  const pullTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pullBackoffRef = useRef(0);
  // Lets that timer reach `resync` without a declaration cycle (same trick as
  // drainRef) — it re-drains first, since a pull with a full queue is unsafe.
  const resyncRef = useRef<(() => Promise<void>) | null>(null);
  // Set when an entry was dropped because the server refused it: local state may
  // now disagree with the server, so a full pull runs once the queue empties.
  const needsResyncRef = useRef(false);
  // Snapshot writes stay off until the cache has been read, so startup can't
  // overwrite good data on disk with the empty initial state.
  const armedRef = useRef(false);
  // The one-way version of the same idea: set by wipeLocal (account deletion),
  // never cleared. Every writer below checks it, so a debounce that was already
  // scheduled — or a drain still landing — can't put the erased cache back.
  const wipedRef = useRef(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Lets the retry timer reach the current drain without a declaration cycle.
  const drainRef = useRef<(() => Promise<void>) | null>(null);

  // `update` must stay stable (every mutation depends on it) while the save
  // scheduler depends on userKey, so it's reached through a ref.
  const scheduleSaveRef = useRef<(() => void) | null>(null);

  /** The single writer: compute the next state from the freshest snapshot, keep
     stateRef in lock-step, schedule the React update, and queue a disk write. */
  const update = useCallback((fn: (s: AppState) => AppState) => {
    const next = fn(stateRef.current);
    stateRef.current = next;
    setState(next);
    scheduleSaveRef.current?.();
  }, []);

  const scheduleSave = useCallback(() => {
    if (!userKey || !armedRef.current || wipedRef.current || saveTimer.current) return;
    saveTimer.current = setTimeout(() => {
      saveTimer.current = null;
      void persist.saveState(userKey, stateRef.current);
    }, SAVE_DEBOUNCE_MS);
  }, [userKey, persist]);
  scheduleSaveRef.current = scheduleSave;

  /** Write the snapshot now — on backgrounding and unmount, where a pending
     debounce would never fire. */
  const flushSave = useCallback(() => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    if (!userKey || !armedRef.current || wipedRef.current) return;
    void persist.saveState(userKey, stateRef.current);
  }, [userKey, persist]);

  // Called after every queue mutation, so it's also the one place that has to
  // republish how much the server still owes us.
  const persistQueue = useCallback(() => {
    setSync({ pending: queueRef.current.length });
    if (!userKey || wipedRef.current) return;
    void persist.saveOutbox(userKey, queueRef.current);
  }, [userKey, setSync, persist]);

  /* ---------- server response merges ---------- */

  const mergeTask = useCallback(
    (srv: ServerTask) => update((s) => ({ ...s, tasks: upsertTask(s.tasks, toTask(srv)) })),
    [update],
  );
  const mergeProject = useCallback(
    (srv: ServerProject) => update((s) => ({ ...s, projects: upsertProject(s.projects, toProject(srv)) })),
    [update],
  );
  const mergeTag = useCallback(
    (srv: ServerTag) => update((s) => ({ ...s, tags: upsertTag(s.tags, toTag(srv)) })),
    [update],
  );
  const mergeHabit = useCallback(
    (srv: ServerHabit) => update((s) => ({ ...s, habits: upsertHabit(s.habits, toHabit(srv)) })),
    [update],
  );
  /** Merge a complete/skip response — the chain step. The resolved row is merged
     by id; the history snapshot takes over the optimistic row we inserted under
     `snapshotTempId`. When the server left no snapshot (the task didn't repeat, or
     its rule ran out) that optimistic row is dropped, so a local guess that
     diverged from the server heals itself here. */
  const mergeResolution = useCallback(
    (res: TaskResolution, snapshotTempId: string) =>
      update((s) => {
        const base = res.snapshot
          ? replaceOrAppend(s.tasks, snapshotTempId, toTask(res.snapshot))
          : s.tasks.filter((t) => t.id !== snapshotTempId);
        return { ...s, tasks: upsertTask(base, toTask(res.task)) };
      }),
    [update],
  );

  /** Swap a temp/old task id for the server-assigned one (create + restore). */
  const reconcileTask = useCallback(
    (oldId: string, srv: ServerTask) =>
      update((s) => {
        const mapped = toTask(srv);
        return {
          ...s,
          tasks: s.tasks.map((t) => (t.id === oldId ? mapped : t)),
          pinnedByDay: renamePin(s.pinnedByDay, oldId, mapped.id),
        };
      }),
    [update],
  );
  /** Swap a temp project id for the server-assigned one. Unlike a task there are no
     pins to rename — but every task filed under the temp id has to follow, or a
     task created into a brand-new project would come loose the moment the project was
     acknowledged. (The queued ops pointing at it are rewritten separately, by
     remapIds.) */
  const reconcileProject = useCallback(
    (oldId: string, srv: ServerProject) =>
      update((s) => {
        const mapped = toProject(srv);
        return {
          ...s,
          projects: s.projects.map((l) => (l.id === oldId ? mapped : l)),
          tasks: s.tasks.map((t) => (t.projectId === oldId ? { ...t, projectId: mapped.id } : t)),
        };
      }),
    [update],
  );
  /** Swap a temp tag id for the server-assigned one. Every task carrying the
     temp id has to follow, exactly as with a project — otherwise a task tagged with
     a brand-new tag comes loose the moment the tag create is acknowledged.
     (The queued ops pointing at it are rewritten separately, by remapIds.)

     One wrinkle a project doesn't have: the server is idempotent on a tag's name,
     so the id coming back may be one this device ALREADY holds — two devices
     both typing `@errand` resolve to a single row. Rewriting can therefore
     produce a duplicate inside a task's set, so the mapping dedupes. */
  const reconcileTag = useCallback(
    (oldId: string, srv: ServerTag) =>
      update((s) => {
        const mapped = toTag(srv);
        return {
          ...s,
          // upsert rather than map: when the server handed back a tag this
          // device already has, the temp row is dropped instead of duplicated
          tags: upsertTag(
            s.tags.filter((t) => t.id !== oldId),
            mapped,
          ),
          tasks: s.tasks.map((t) => {
            const ids = t.tagIds ?? [];
            if (!ids.includes(oldId)) return t;
            return { ...t, tagIds: [...new Set(ids.map((id) => (id === oldId ? mapped.id : id)))] };
          }),
        };
      }),
    [update],
  );
  const reconcileHabit = useCallback(
    (oldId: string, srv: ServerHabit) =>
      update((s) => {
        const mapped = toHabit(srv);
        return {
          ...s,
          habits: s.habits.map((h) => (h.id === oldId ? mapped : h)),
          pinnedByDay: renamePin(s.pinnedByDay, oldId, mapped.id),
        };
      }),
    [update],
  );

  const handlers = useMemo<OpHandlers>(
    () => ({
      mergeTask,
      mergeProject,
      mergeTag,
      mergeHabit,
      mergeResolution,
      reconcileTask,
      reconcileProject,
      reconcileTag,
      reconcileHabit,
    }),
    [
      mergeTask,
      mergeProject,
      mergeTag,
      mergeHabit,
      mergeResolution,
      reconcileTask,
      reconcileProject,
      reconcileTag,
      reconcileHabit,
    ],
  );

  /* ---------- sync + queue drain ---------- */

  /** Wake up and try the whole reconciliation again, on the same capped
     exponential backoff the queue uses: 1s, 2s, 4s … 60s. */
  const schedulePullRetry = useCallback(() => {
    if (pullTimer.current) return; // one pending wake-up is enough
    const delay = Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** pullBackoffRef.current++);
    pullTimer.current = setTimeout(() => {
      pullTimer.current = null;
      void resyncRef.current?.().catch(() => {});
    }, delay);
  }, []);

  /** Pull the whole server state and replace local. Only safe with an empty
     queue — it would otherwise overwrite writes that haven't been sent yet. */
  const pullAll = useCallback(async () => {
    setSync({ phase: 'syncing' });
    try {
      const res = await apiRef.current.get<SyncResponse>(`/v1/sync?today=${todayStr()}`);
      update((s) => toAppState(res, s.cap));
      // The server is authoritative on entitlement (docs/subscriptions.md §7),
      // and THIS is where it speaks. Adopting here also re-stamps the cache's
      // age, which is what the 7-day offline grace measures against (§5).
      // Guarded because an older server may not send the field at all — absent
      // is "no opinion", not "not entitled".
      if (res.plan) adoptPlanRef.current(res.plan, res.planReason ?? null, res.planStore ?? null);
      if (pullTimer.current) {
        clearTimeout(pullTimer.current);
        pullTimer.current = null;
      }
      pullBackoffRef.current = 0;
      setSync({ phase: 'synced', lastSyncedAt: Date.now() });
    } catch (err) {
      setSync({ phase: phaseForError(err) });
      schedulePullRetry();
      throw err;
    }
  }, [update, setSync, schedulePullRetry]);

  const scheduleDrain = useCallback((delayMs: number) => {
    if (retryTimer.current) return; // one pending wake-up is enough
    retryTimer.current = setTimeout(() => {
      retryTimer.current = null;
      void drainRef.current?.();
    }, delayMs);
  }, []);

  /** Send queued mutations in order until the queue empties or the head can't be
     delivered. Never throws: nothing in the UI should have to handle a failed
     background write. Awaiting it always means "the queue has been worked" —
     concurrent callers join the run in progress rather than starting a second. */
  const drain = useCallback((): Promise<void> => {
    if (drainPromiseRef.current) return drainPromiseRef.current;
    const run = async () => {
      // Did anything actually reach the server this run? A resolved send and a
      // rejection both count — the server answered either way; only a fetch
      // that never completed doesn't. It's what decides whether we may claim a
      // fresh `lastSyncedAt` at the end instead of asserting a sync we never had.
      let reached = false;
      if (queueRef.current.length > 0) setSync({ phase: 'syncing' });
      while (queueRef.current.length > 0) {
        const entry = queueRef.current[0];
        if (!entry) break; // the length check above makes this unreachable; it narrows the type
        try {
          const mapping = await performOp(apiRef.current, entry.op, handlers);
          reached = true;
          const rest = queueRef.current.slice(1);
          // A create just learned its real id — rewrite everything still queued
          // behind it before any of those entries hit the wire.
          queueRef.current = mapping
            ? rest.map((e) => ({ ...e, op: remapIds(e.op, mapping.from, mapping.to) }))
            : rest;
          backoffRef.current = 0;
          persistQueue();
        } catch (err) {
          if (err instanceof ApiError) reached = true;
          const disposition = dispositionFor(err);
          const exhausted =
            disposition === 'retry-limited' && entry.tries + 1 >= MAX_SERVER_REJECTS;
          if (disposition !== 'drop' && !exhausted) {
            if (disposition === 'retry-limited') {
              queueRef.current = [
                { ...entry, tries: entry.tries + 1 },
                ...queueRef.current.slice(1),
              ];
              persistQueue();
            }
            // Stop at the head rather than skipping it: later entries can depend
            // on this one (a patch on a row still being created, a second chain
            // advance), so out-of-order delivery is worse than waiting.
            setSync({ phase: phaseForError(err) });
            scheduleDrain(Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** backoffRef.current++));
            return;
          }
          // Give up on this entry. `isGone` means the target was already deleted
          // elsewhere — the outcome we wanted — so only a real refusal implies
          // local state and the server have diverged.
          queueRef.current = queueRef.current.slice(1);
          if (!isGone(err)) needsResyncRef.current = true;
          persistQueue();
        }
      }
      if (needsResyncRef.current) {
        needsResyncRef.current = false;
        // Heal the divergence a dropped entry left behind. If the pull itself
        // fails we're offline; keep the flag so the next drain retries.
        // pullAll publishes its own phase either way.
        await pullAll().catch(() => {
          needsResyncRef.current = true;
        });
      } else if (reached) {
        setSync({ phase: 'synced', lastSyncedAt: Date.now() });
      }
    };
    const p = run().finally(() => {
      drainPromiseRef.current = null;
    });
    drainPromiseRef.current = p;
    return p;
  }, [handlers, persistQueue, pullAll, scheduleDrain, setSync]);
  drainRef.current = drain;

  /** Queue a mutation. Persisted before the network is touched, so the intent
     outlives a crash even if the request never goes out. */
  const enqueue = useCallback(
    (op: OutboxOp) => {
      queueRef.current = [...queueRef.current, { seq: seqRef.current++, op, tries: 0 }];
      persistQueue();
      void drain();
    },
    [drain, persistQueue],
  );

  /** Bring local state in line with the server: flush what we owe it first, then
     pull. Skips the pull while writes are still stuck, since replacing state
     would discard them. */
  const resync = useCallback(async () => {
    await drain();
    if (queueRef.current.length > 0) return;
    await pullAll();
  }, [drain, pullAll]);
  resyncRef.current = resync;

  /** Erase this account's cache and stop writing it — account deletion's local
     half (docs/account-deletion.md). Order matters: disarm first, cancel the
     timers that would otherwise fire into a deleted account, THEN clear the
     keys. Doing it the other way round races the 500ms snapshot debounce, which
     would faithfully write the state back moments after we removed it.

     In-memory state is left alone on purpose — the provider unmounts a beat
     later when auth flips to signed-out, and blanking the screen underneath the
     user mid-request would read as a crash rather than as progress. */
  const wipeLocal = useCallback(async () => {
    wipedRef.current = true;
    armedRef.current = false;
    for (const t of [saveTimer, retryTimer, pullTimer]) {
      if (t.current) {
        clearTimeout(t.current);
        t.current = null;
      }
    }
    if (userKey) await persist.clearPersisted(userKey);
  }, [userKey, persist]);

  /** The corner badge's tap: the same reconciliation, made safe to hand to a
     button. Failures are swallowed — the phase already records them, and a
     background write has nothing to throw at — and a second tap joins nothing
     rather than starting a second pull. */
  const manualRef = useRef<Promise<void> | null>(null);
  const syncNow = useCallback(() => {
    if (manualRef.current) return;
    manualRef.current = resync()
      .catch(() => {})
      .finally(() => {
        manualRef.current = null;
      });
  }, [resync]);

  /** Launch-unique local id for a row the server hasn't named yet. */
  const nextTempId = useCallback(() => `tmp-${LAUNCH_TAG}-${tempSeq.current++}`, []);

  // Startup: paint the cache, flush what we owe the server, then pull. Auth is
  // already resolved (the Gate mounts this only when signed in).
  useEffect(() => {
    let alive = true;
    (async () => {
      if (userKey) {
        const [cached, queued] = await Promise.all([
          persist.loadState(userKey),
          persist.loadOutbox(userKey),
        ]);
        if (!alive) return;
        if (queued.length > 0) {
          queueRef.current = queued;
          seqRef.current = Math.max(...queued.map((e) => e.seq)) + 1;
          // Writes restored off disk are owed to the server from the first
          // frame, so the badge shows them before the drain below runs.
          setSync({ pending: queued.length });
        }
        if (cached) {
          update(() => cached);
          // Paint immediately — the pull below only refines what's already shown,
          // and on a dead network there may not be one.
          setReady(true);
        }
      }
      armedRef.current = true;
      // Pending writes go BEFORE the pull: a pull replaces local state, so
      // draining first is what stops a server that hasn't heard about an offline
      // tap from overwriting it.
      await drain();
      if (!alive) return;
      if (queueRef.current.length === 0) {
        try {
          await pullAll();
        } catch {
          // Offline. The cache stands (or an empty app, still usable); the next
          // action or foreground return retries.
        }
      }
      if (alive) setReady(true);
    })();
    return () => {
      alive = false;
    };
  }, [userKey, drain, pullAll, update, setSync, persist]);

  // Returning to the foreground is the most likely moment for connectivity to
  // have come back; leaving is the last chance to write the snapshot.
  useEffect(
    () =>
      lifecycle.subscribe((next) => {
        if (next === 'active') void drain();
        else flushSave();
      }),
    [lifecycle, drain, flushSave],
  );

  // Unmount (sign-out) — persist what's in memory and stop the retry timer.
  useEffect(
    () => () => {
      if (retryTimer.current) clearTimeout(retryTimer.current);
      if (pullTimer.current) clearTimeout(pullTimer.current);
      flushSave();
    },
    [flushSave],
  );

  /* ---------- selectors (pure, read the freshest snapshot) ---------- */
  const findItem = useCallback((id: string) => logic.findItem(stateRef.current, id), []);
  const pinnedItem = useCallback((day: string) => logic.pinnedItem(stateRef.current, day), []);
  const pinnedIdFor = useCallback((day: string) => logic.pinnedIdFor(stateRef.current, day), []);
  const tasksForDay = useCallback((day: string) => logic.tasksForDay(stateRef.current, day), []);
  const doneForDay = useCallback((day: string) => logic.doneForDay(stateRef.current, day), []);
  const skippedForDay = useCallback((day: string) => logic.skippedForDay(stateRef.current, day), []);
  const habitsForDay = useCallback(
    (day: string) => logic.habitsForDay(stateRef.current, day, weekStartsOn),
    [weekStartsOn],
  );
  const dayActivityCounts = useCallback(
    (start: string, end: string) => logic.dayActivityCounts(stateRef.current, start, end),
    [],
  );
  const overdue = useCallback(() => logic.overdue(stateRef.current), []);
  const backlog = useCallback(
    (scope?: Scope | null) => logic.backlog(stateRef.current, scope),
    [],
  );
  const upcoming = useCallback(() => logic.upcoming(stateRef.current), []);
  const isAllDone = useCallback(() => logic.isAllDone(stateRef.current), []);
  const activeProjects = useCallback(() => logic.activeProjects(stateRef.current), []);
  const archivedProjects = useCallback(() => logic.archivedProjects(stateRef.current), []);
  const tasksInProject = useCallback((projectId: string) => logic.tasksInProject(stateRef.current, projectId), []);
  const activeTags = useCallback(() => logic.activeTags(stateRef.current), []);
  const tagsOfTask = useCallback((task: Task) => logic.tagsOfTask(stateRef.current, task), []);
  const tasksWithTag = useCallback((tagId: string) => logic.tasksWithTag(stateRef.current, tagId), []);
  const tagOpenCount = useCallback((tagId: string) => logic.tagOpenCount(stateRef.current, tagId), []);
  const tasksInScope = useCallback((scope: Scope) => logic.tasksInScope(stateRef.current, scope), []);
  const scopeProgress = useCallback((scope: Scope) => logic.scopeProgress(stateRef.current, scope), []);
  const projectProgress = useCallback(
    (projectId: string) => logic.projectProgress(stateRef.current, projectId),
    [],
  );
  const projectOpenCount = useCallback(
    (projectId: string) => logic.projectOpenCount(stateRef.current, projectId),
    [],
  );
  const hueForTask = useCallback((task: Task) => logic.hueForTask(stateRef.current, task), []);
  const nextProjectHue = useCallback(() => logic.nextProjectHue(stateRef.current), []);
  const nextTagHue = useCallback(() => logic.nextTagHue(stateRef.current), []);
  /** The habit list in the user's own order — every habit surface reads it
     through here rather than touching `state.habits` directly, so the order is
     one rule in one place. */
  const orderedHabits = useCallback(() => logic.orderedHabits(stateRef.current), []);
  const projectForTask = useCallback((task: Task) => logic.projectForTask(stateRef.current, task), []);

  /* ---------- mutations (optimistic transform + queued write) ---------- */

  const completeItem = useCallback(
    (id: string, day: string = todayStr()): { celebrated: boolean } => {
      const prev = stateRef.current;
      const rec = logic.findItem(prev, id);
      // Completing a repeating task advances the chain and leaves a history row
      // behind; it goes in under this temp id until the server names it.
      const snapshotId = nextTempId();
      // Habits record a per-day check-in; tasks resolve. The two are different
      // enough (a check-in is reversible per day, a resolution moves a chain) that
      // the logic layer keeps them apart — this is where the UI's one "complete"
      // gesture picks which one it meant.
      const { state: next, celebrated } =
        rec?.project === 'habits'
          ? logic.checkInHabit(prev, id, day, weekStartsOn)
          : logic.completeTask(prev, id, snapshotId);
      update(() => next);
      if (rec) {
        // `today` is always the client's real today — the server derives the
        // returned habit's state (streak/atRisk) against it; `day` is the day the
        // check-in itself lands on (the viewed date). Both are captured now, not
        // when the entry drains, so a queued action still records the day it
        // happened on even if it sends after midnight.
        const today = todayStr();
        if (rec.project === 'habits') {
          // decide check-in vs un-check-in from the VIEWED day's prior state
          const wasDone = prev.checkins[id]?.[day]?.completed ?? false;
          enqueue(
            wasDone
              ? { kind: 'habit.uncheckin', id, day, today }
              : { kind: 'habit.checkin', id, day, today },
          );
          if (!wasDone) analytics?.track('habit_checkin');
        } else {
          const wasDone = (rec.item as Task).done;
          enqueue(
            wasDone ? { kind: 'task.reopen', id } : { kind: 'task.complete', id, today, snapshotId },
          );
          if (!wasDone) analytics?.track('task_completed', { repeating: !!(rec.item as Task).rrule });
        }
      }
      return { celebrated };
    },
    [update, enqueue, nextTempId, weekStartsOn, analytics],
  );

  const togglePin = useCallback(
    (id: string, day: string) => {
      const prev = stateRef.current;
      const clearing = prev.pinnedByDay[day] === id;
      const rec = logic.findItem(prev, id);
      const task = prev.tasks.find((t) => t.id === id);
      const needsDay = !clearing && !!task && task.scheduledFor === null;
      update((s) => logic.togglePin(s, id, day));
      if (clearing) {
        enqueue({ kind: 'pin.clear', day });
      } else if (rec) {
        enqueue({
          kind: 'pin.set',
          day,
          itemType: rec.project === 'habits' ? 'habit' : 'task',
          itemId: id,
          scheduleFirst: needsDay,
        });
      }
    },
    [update, enqueue],
  );

  const toggleStep = useCallback(
    (taskId: string, index: number) => {
      const next = logic.toggleStep(stateRef.current, taskId, index);
      update(() => next);
      const t = next.tasks.find((x) => x.id === taskId);
      if (t) enqueue({ kind: 'task.patch', id: taskId, patch: { subSteps: t.subSteps } });
    },
    [update, enqueue],
  );

  const moveLastToLater = useCallback((): boolean => {
    const { state: next, movedId } = logic.moveLastToLater(stateRef.current);
    if (!movedId) return false;
    update(() => next);
    enqueue({ kind: 'task.reschedule', id: movedId, scheduledFor: null });
    return true;
  }, [update, enqueue]);

  const rescheduleTask = useCallback(
    (id: string, day: string | null) => {
      // For a repeating task this also re-anchors its cadence — the rule is
      // anchored on the row's own day (same as Todoist).
      // read the rank off the TRANSFORMED task rather than recomputing it — one
      // source of truth, so the optimistic row and the queued op can't diverge
      const next = logic.rescheduleTask(stateRef.current, id, day);
      update(() => next);
      const moved = next.tasks.find((t) => t.id === id);
      enqueue({
        kind: 'task.reschedule',
        id,
        scheduledFor: day,
        ...(moved?.sortOrder === undefined ? {} : { sortOrder: moved.sortOrder }),
      });
    },
    [update, enqueue],
  );

  const createProject = useCallback(
    (name: string, hue: HueName): string => {
      const id = nextTempId();
      update((s) => logic.createProject(s, id, name, hue));
      enqueue({ kind: 'project.create', tempId: id, body: { name, hue } });
      return id;
    },
    [update, enqueue, nextTempId],
  );

  /** Create a tag, or return the id of the one already carrying that name.
     Idempotent locally the way it is server-side — fast entry types a tag into
     being, so typing `@errand` when `@Errand` exists must mean that one rather
     than making a near-duplicate nobody can tell apart in the picker. Returning
     the existing id (and queueing nothing) is what makes the two agree. */
  const createTag = useCallback(
    (name: string): string => {
      const existing = logic.tagByName(stateRef.current, name);
      // Retyping a name means THAT tag — and it keeps the colour it has. The
      // server makes the same call on its idempotent insert.
      if (existing) return existing.id;
      const id = nextTempId();
      // The colour is picked HERE rather than by each caller, so every way a tag
      // can be born — the capture sheet's composer, an `@errand` token resolved
      // on save, the manager's + — assigns one the same way and no path can
      // create a colourless tag.
      const hue = logic.nextTagHue(stateRef.current);
      update((s) => logic.createTag(s, id, name, hue));
      enqueue({ kind: 'tag.create', tempId: id, body: { name, hue } });
      return id;
    },
    [update, enqueue, nextTempId],
  );

  const updateTag = useCallback(
    (id: string, patch: { name?: string; hue?: HueName }) => {
      // Renaming or recolouring a tag costs no task write — tasks hold the id,
      // not the word or the colour.
      update((s) => logic.updateTag(s, id, patch));
      enqueue({ kind: 'tag.patch', id, patch });
    },
    [update, enqueue],
  );

  const deleteTag = useCallback(
    (id: string) => {
      // Deleting a tag never deletes work: it's stripped from its tasks here
      // and, in the same transaction, server-side — so no per-task op is queued.
      update((s) => logic.deleteTag(s, id));
      enqueue({ kind: 'tag.delete', id });
    },
    [update, enqueue],
  );

  const updateProject = useCallback(
    (id: string, patch: { name?: string; hue?: HueName }) => {
      // Recolouring a project recolours every task filed under it, with no write to
      // a single task row — the tasks inherit rather than copy (see hueForTask).
      update((s) => logic.updateProject(s, id, patch));
      enqueue({ kind: 'project.patch', id, patch });
    },
    [update, enqueue],
  );

  const setProjectArchived = useCallback(
    (id: string, archived: boolean) => {
      update((s) => logic.setProjectArchived(s, id, archived));
      enqueue({ kind: archived ? 'project.archive' : 'project.unarchive', id });
    },
    [update, enqueue],
  );

  const deleteProject = useCallback(
    (id: string) => {
      // Deleting a project never deletes work: its tasks are unfiled here and, in
      // the same transaction, server-side — so no per-task op is queued.
      update((s) => logic.deleteProject(s, id));
      enqueue({ kind: 'project.delete', id });
    },
    [update, enqueue],
  );

  const updateTask = useCallback(
    (
      id: string,
      patch: {
        name?: string;
        hue?: HueName;
        projectId?: string | null;
        tagIds?: string[];
        rrule?: string | null;
        remindAt?: number | null;
      },
    ) => {
      update((s) => logic.updateTask(s, id, patch));
      enqueue({ kind: 'task.patch', id, patch });
    },
    [update, enqueue],
  );

  const updateHabit = useCallback(
    (
      id: string,
      patch: {
        name?: string;
        emoji?: string;
        hue?: HueName;
        target?: number;
        schedule?: HabitSchedule;
      },
    ) => {
      // The cadence goes over the wire as given; both sides normalize it (here via
      // logic.updateHabit, there in HabitService), so the optimistic row and the
      // server's echo agree on one shape.
      update((s) => logic.updateHabit(s, id, patch, weekStartsOn));
      enqueue({ kind: 'habit.patch', id, patch });
    },
    [update, enqueue, weekStartsOn],
  );

  // Order is a client-only concern (no server field), so this stays local.
  /**
   * Move a task within a list, committing the new manual order.
   *
   * `list` is the list as it reads BEFORE the move — the caller (a screen) is
   * the only place that knows WHICH list was dragged, so it hands one over
   * rather than the store guessing. `moveWithin` returns the single row to
   * write, or a whole renumbering on the rare occasion the target gap can no
   * longer be split.
   *
   * Each rank is its own `task.patch`, so the normal case is exactly one queued
   * op per drag. That is the property the fractional rank buys, and why this
   * isn't a dense index — see docs/task-ordering.md.
   */
  const moveTask = useCallback(
    (list: Task[], from: number, to: number) => {
      const ranks = logic.moveWithin(list, from, to);
      if (ranks.length === 0) return;
      update((s) => logic.applyRanks(s, ranks));
      for (const r of ranks) enqueue({ kind: 'task.patch', id: r.id, patch: { sortOrder: r.sortOrder } });
    },
    [update, enqueue],
  );

  /**
   * Move a habit within the list. The counterpart of `moveTask`, and the fix for
   * a bug that shipped with the habit drag: `reorderHabits` used to rearrange
   * the local array and queue NOTHING, so the next full sync handed the habits
   * back in server order and the arrangement vanished. It now writes a rank and
   * enqueues it, exactly as a task move does.
   */
  const moveHabit = useCallback(
    (list: Habit[], from: number, to: number) => {
      const ranks = logic.moveWithin(list, from, to);
      if (ranks.length === 0) return;
      update((s) => logic.applyHabitRanks(s, ranks));
      for (const r of ranks) enqueue({ kind: 'habit.patch', id: r.id, patch: { sortOrder: r.sortOrder } });
    },
    [update, enqueue],
  );

  const deleteHabit = useCallback(
    (id: string) => {
      update((s) => logic.deleteHabit(s, id));
      enqueue({ kind: 'habit.delete', id });
    },
    [update, enqueue],
  );

  // No un-delete endpoint: recreate from the snapshot and reconcile to the new
  // server id. (Check-in history tied to the old id is not recovered.)
  const restoreHabit = useCallback(
    (habit: Habit, index: number) => {
      update((s) => logic.restoreHabit(s, habit, index));
      enqueue({
        kind: 'habit.create',
        tempId: habit.id,
        body: {
          name: habit.name,
          hue: habit.hue,
          ...(habit.emoji ? { emoji: habit.emoji } : {}),
          target: habit.target,
          // The restored habit keeps its cadence and its original start day, so
          // consistency isn't re-based to now (its history is gone with the old
          // id, but the yardstick it was measured against survives the undo).
          schedule: habit.schedule,
          startsOn: habit.startsOn,
          // ...and its place in the list. An undo should put the habit back where
          // it was, not append it — the snapshot carries the rank it had.
          sortOrder: logic.rankOf(habit),
        },
      });
    },
    [update, enqueue],
  );

  // A history row reopens like any plain task (it *is* a plain task); the live
  // repeating row it came from keeps its own future day.
  const undoDone = useCallback(
    (id: string) => {
      update((s) => logic.undoDone(s, id));
      enqueue({ kind: 'task.reopen', id });
    },
    [update, enqueue],
  );

  const skipTask = useCallback(
    (id: string) => {
      // Skipping a repeating task advances the chain too — the skipped day becomes
      // history under this temp id and the row moves on.
      const snapshotId = nextTempId();
      update((s) => logic.skipTask(s, id, snapshotId));
      enqueue({ kind: 'task.skip', id, today: todayStr(), snapshotId });
    },
    [update, enqueue, nextTempId],
  );

  const unskipTask = useCallback(
    (id: string) => {
      update((s) => logic.unskipTask(s, id));
      enqueue({ kind: 'task.unskip', id });
    },
    [update, enqueue],
  );

  const deleteTask = useCallback(
    (id: string) => {
      update((s) => logic.deleteTask(s, id));
      enqueue({ kind: 'task.delete', id });
    },
    [update, enqueue],
  );

  // Recreate from the snapshot and reconcile the id (see restoreHabit). Only the
  // create-able fields survive; a restored task returns active.
  const restoreTask = useCallback(
    (task: Task) => {
      update((s) => logic.restoreTask(s, task));
      enqueue({
        kind: 'task.create',
        tempId: task.id,
        body: {
          name: task.name,
          hue: task.hue,
          // a restored task returns to the project it was filed under, carrying
          // the tags it had — an undo should put back what was deleted
          projectId: task.projectId ?? null,
          tagIds: task.tagIds ?? [],
          scheduledFor: task.scheduledFor,
          subSteps: task.subSteps,
          // a restored repeating task keeps repeating, from its own day
          rrule: task.rrule ?? null,
          // ...and a restored timed task keeps its time (the server drops it
          // anyway if the restore lands in the backlog)
          remindAt: task.remindAt ?? null,
          today: todayStr(),
          // ...and its place in the manual order. An undo should put the task
          // back where it was, not at the top of the queue.
          sortOrder: logic.rankOf(task),
        },
      });
    },
    [update, enqueue],
  );

  const replanTo = useCallback(
    (id: string, where: ReplanWhere) => {
      const prev = stateRef.current;
      // Done / Skipped resolve the task (advancing the chain when it repeats), so
      // they need a temp id for the history row, exactly like the row actions.
      const snapshotId = nextTempId();
      const next = logic.replanTo(prev, id, where, snapshotId);
      update(() => next);
      const t = next.tasks.find((x) => x.id === id);
      if (!t) return;
      const today = todayStr();
      if (where === 'Done') {
        enqueue({ kind: 'task.complete', id, today, snapshotId });
      } else if (where === 'Skipped') {
        enqueue({ kind: 'task.skip', id, today, snapshotId });
      } else {
        // Today / Tomorrow / This weekend / Someday — logic already set the day
        // AND re-ranked into the destination list; carry both in one op
        enqueue({
          kind: 'task.reschedule',
          id,
          scheduledFor: t.scheduledFor,
          ...(t.sortOrder === undefined ? {} : { sortOrder: t.sortOrder }),
        });
      }
    },
    [update, enqueue, nextTempId],
  );

  const saveCapture = useCallback(
    (name: string, cap: CaptureState, day?: string): string => {
      const d = day ?? todayStr();
      const tempId = nextTempId();
      update((s) => logic.saveCapture(s, tempId, name, cap, d, weekStartsOn));
      if (cap.habit) {
        enqueue({
          kind: 'habit.create',
          tempId,
          body: {
            name,
            hue: cap.hue,
            ...(cap.emoji ? { emoji: cap.emoji } : {}),
            target: Math.max(1, cap.target ?? 1),
            schedule: cap.schedule,
            // A habit begins today whichever day is on screen — only the client
            // knows its own local day, and days before it are never misses.
            startsOn: todayStr(),
            // the same rank the optimistic row took — a new habit appends
            sortOrder: logic.rankForNewHabit(stateRef.current),
          },
        });
      } else {
        // One task row either way — a recurring capture just carries an `rrule`,
        // anchored on the day it's scheduled for (so it needs a day; mirrors
        // logic.saveCapture).
        const rrule = cap.recurrence ? presetToRRule(cap.recurrence) : null;
        const scheduledFor = cap.today || rrule ? d : null;
        enqueue({
          kind: 'task.create',
          tempId,
          body: {
            name,
            hue: cap.hue,
            // filing is optional; `null` means unfiled, not "missing". Note this
            // may still be a TEMP project id when the user made the project in the same
            // gesture — remapIds rewrites it once the project create is acknowledged.
            projectId: cap.projectId,
            // may hold TEMP ids for tags created in the same gesture — remapIds
            // rewrites them as each tag create is acknowledged, exactly as it
            // does for a brand-new project above
            tagIds: cap.tagIds,
            scheduledFor,
            subSteps: [],
            rrule,
            // a time needs a day to resolve to an instant, so a backlog capture
            // drops it (the server enforces the same rule — belt and braces, so
            // the optimistic row and the server's echo agree)
            remindAt: scheduledFor ? cap.remindAt : null,
            today: d,
            // the same rank the optimistic row took, so the server agrees
            sortOrder: scheduledFor
              ? logic.rankForDay(stateRef.current, scheduledFor, cap.remindAt)
              : logic.rankForBacklog(stateRef.current, cap.projectId),
            },
        });
      }
      return tempId;
    },
    [update, enqueue, nextTempId, weekStartsOn],
  );

  const value = useMemo<StoreValue>(
    () => ({
      state,
      findItem,
      pinnedItem,
      pinnedIdFor,
      tasksForDay,
      doneForDay,
      skippedForDay,
      habitsForDay,
      dayActivityCounts,
      overdue,
      backlog,
      upcoming,
      activeProjects,
      archivedProjects,
      tasksInProject,
      projectProgress,
      projectOpenCount,
      nextProjectHue,
      nextTagHue,
      orderedHabits,
      hueForTask,
      projectForTask,
      createProject,
      updateProject,
      setProjectArchived,
      deleteProject,
      activeTags,
      tagsOfTask,
      tasksWithTag,
      tagOpenCount,
      tasksInScope,
      scopeProgress,
      createTag,
      updateTag,
      deleteTag,
      completeItem,
      togglePin,
      toggleStep,
      moveLastToLater,
      replanTo,
      rescheduleTask,
      updateTask,
      updateHabit,
      moveTask,
      moveHabit,
      deleteHabit,
      restoreHabit,
      skipTask,
      unskipTask,
      deleteTask,
      restoreTask,
      undoDone,
      saveCapture,
      isAllDone,
      resync,
      wipeLocal,
    }),
    [state, findItem, pinnedItem, pinnedIdFor, tasksForDay, doneForDay, skippedForDay, habitsForDay, dayActivityCounts, overdue, backlog, upcoming, activeProjects, archivedProjects, tasksInProject, projectProgress, projectOpenCount, nextProjectHue, nextTagHue, orderedHabits, hueForTask, projectForTask, createProject, updateProject, setProjectArchived, deleteProject, activeTags, tagsOfTask, tasksWithTag, tagOpenCount, tasksInScope, scopeProgress, createTag, updateTag, deleteTag, completeItem, togglePin, toggleStep, moveLastToLater, replanTo, rescheduleTask, updateTask, updateHabit, moveTask, moveHabit, deleteHabit, restoreHabit, skipTask, unskipTask, deleteTask, restoreTask, undoDone, saveCapture, isAllDone, resync, wipeLocal],
  );

  const syncValue = useMemo<SyncStatus>(() => ({ ...syncSnap, syncNow }), [syncSnap, syncNow]);

  if (!ready) return <>{fallback ?? null}</>;

  return (
    <StoreContext.Provider value={value}>
      <SyncContext.Provider value={syncValue}>{children}</SyncContext.Provider>
    </StoreContext.Provider>
  );
}
